import {
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useMemo, useState } from "react";
import { z } from "zod";
import { ConfirmActionModal, ErrorState, LoadingState, StatusBadge } from "@how/ui";
import {
  describeEntitlementError,
  endOfChurchDay,
  isInForce,
  useModuleCatalog,
  useSetEntitlement,
  type CatalogModule,
  type Entitlement,
  type EntitlementStatus,
} from "./entitlements";
import { PlanBadge } from "../plans/PlanBadge";
import {
  describePlanError,
  modulesByPlan,
  PLAN_KEYS,
  PLAN_LABEL,
  usePlanModules,
  useSetTenantPlan,
  type PlanKey,
} from "../plans/plans";

const TRIAL_DAYS = 30;

/** "resume" puts a suspended module back as it was: a trial with its end date, or an open-ended activation. */
type Action = "trial" | "active" | "resume" | "suspended" | "canceled";

/**
 * The changes offered for a module in its current state. A trial can be moved to a new end date by starting it again.
 * A suspended trial whose end has passed has nothing to resume, so it is offered a new trial or an activation (#172).
 */
function actionsFor(entitlement: Entitlement | undefined, now = new Date()): readonly Action[] {
  switch (entitlement?.status ?? null) {
    case null:
    case "canceled":
      return ["trial", "active"];
    case "trial":
      return ["active", "trial", "suspended", "canceled"];
    case "active":
      return ["suspended", "canceled"];
    case "suspended":
      return entitlement?.ends_at != null && new Date(entitlement.ends_at) <= now
        ? ["active", "trial", "canceled"]
        : ["resume", "canceled"];
  }
}

/** The status an action writes. Resuming a row with an end date resumes a trial; without one, an activation. */
function statusFor(action: Action, entitlement: Entitlement | undefined): EntitlementStatus {
  if (action !== "resume") return action;
  return entitlement?.ends_at == null ? "active" : "trial";
}

const ACTION_LABEL: Record<Action, (entitlement: Entitlement | undefined) => string> = {
  trial: (e) => (e?.status === "trial" ? "Change trial end" : "Start trial"),
  active: () => "Activate",
  resume: (e) => (e?.ends_at == null ? "Resume" : "Resume trial"),
  suspended: () => "Suspend",
  canceled: () => "Cancel",
};

/** Today's date where the church is, as YYYY-MM-DD. An unknown zone falls back to UTC, as endOfChurchDay does. */
function churchToday(timeZone: string): string {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

function addDays(date: string, days: number): string {
  const [year = 0, month = 1, day = 1] = date.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

function trialSchema(today: string) {
  return z.object({
    endsOn: z
      .string()
      .min(1, "Choose the day the trial ends.")
      .refine((value) => value > today, "Choose a day after today."),
  });
}

/** A day as the church reads it; an unknown zone falls back to the admin's. */
function churchDay(iso: string, timeZone: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { timeZone });
  } catch {
    return new Date(iso).toLocaleDateString();
  }
}

function stateLine(entitlement: Entitlement | undefined, timeZone: string): string {
  if (entitlement === undefined) return "Not enabled";
  const ends = entitlement.ends_at === null ? null : churchDay(entitlement.ends_at, timeZone);
  if (entitlement.status === "trial") {
    return isInForce(entitlement) ? `Until ${ends ?? "no end date"}` : `Trial ended ${ends ?? ""}`;
  }
  if (entitlement.status === "active")
    return isInForce(entitlement) ? "In use" : `Ended ${ends ?? ""}`;
  return "Hidden from the church; data kept";
}

export interface ModulesCardProps {
  readonly tenantId: string;
  readonly tenantName: string;
  readonly entitlements: readonly Entitlement[];
  /** The church's time zone: a trial ends when the church's day ends (#172). */
  readonly timeZone: string;
  /** The church's plan (ADR 0055): what it includes is on by default; a row of the church's own decides otherwise. */
  readonly plan: PlanKey;
  /** Platform super admins change modules; support staff see them. The function refuses anyone else anyway. */
  readonly canManage: boolean;
}

/** What the church has for an add-on, in the words of ADR 0055 §7: In plan, Added, Turned off for this church, Not included. */
export function planStateLabel(inPlan: boolean, entitlement: Entitlement | undefined): string {
  const on = entitlement !== undefined && isInForce(entitlement);
  if (entitlement === undefined) return inPlan ? "In plan" : "Not included";
  if (on) return inPlan ? "In plan" : "Added";
  return inPlan ? "Turned off for this church" : "Not included";
}

/**
 * A church's modules (Milestone 3 task 17). Every module the platform offers is listed with what the church has:
 * base modules are included, add-ons are started as a trial with an end date, activated, suspended or cancelled
 * through set_module_entitlement. Suspending and cancelling are confirmed, naming the church and the module; the
 * module's data stays either way. A refusal (a module that needs another first, or one another module depends on)
 * is shown in plain words.
 */
export function ModulesCard({
  tenantId,
  tenantName,
  entitlements,
  timeZone,
  plan,
  canManage,
}: ModulesCardProps) {
  const catalog = useModuleCatalog();
  const setEntitlement = useSetEntitlement(tenantId);
  const planModules = usePlanModules();
  const setPlan = useSetTenantPlan(tenantId);
  const [planTo, setPlanTo] = useState<PlanKey | null>(null);
  const inPlan = useMemo(
    () => modulesByPlan(planModules.data ?? [])[plan],
    [planModules.data, plan],
  );
  const [trialFor, setTrialFor] = useState<CatalogModule | null>(null);
  const today = churchToday(timeZone);
  const trialForm = useForm({
    initialValues: { endsOn: addDays(today, TRIAL_DAYS) },
    validate: zod4Resolver(trialSchema(today)),
    validateInputOnChange: true,
  });
  const [confirming, setConfirming] = useState<{
    module: CatalogModule;
    status: "suspended" | "canceled";
  } | null>(null);

  const byKey = useMemo(
    () => new Map(entitlements.map((e) => [e.module_key, e] as const)),
    [entitlements],
  );
  const names = useMemo(
    () => new Map((catalog.data ?? []).map((m) => [m.key, m.display_name] as const)),
    [catalog.data],
  );
  const nameOf = (key: string) => names.get(key) ?? key;

  const apply = (module: CatalogModule, status: EntitlementStatus, endsAt?: string) => {
    setEntitlement.mutate(
      {
        moduleKey: module.key,
        status,
        ...(endsAt === undefined ? {} : { endsAt }),
        current: byKey.get(module.key),
      },
      {
        onSuccess: () => {
          setTrialFor(null);
          setConfirming(null);
          notifications.show({
            color: "teal",
            title: `${module.display_name} for ${tenantName}`,
            message:
              status === "trial"
                ? `On trial until ${churchDay(endsAt ?? "", timeZone)}.`
                : status === "active"
                  ? "Active. Staff and members see it now."
                  : status === "suspended"
                    ? "Suspended. It is hidden from staff and members, and its data is kept."
                    : "Cancelled. It is hidden from staff and members, and its data is kept.",
          });
        },
        onError: (error) => {
          notifications.show({
            color: "red",
            title: `${module.display_name} was not changed`,
            message: describeEntitlementError(error, nameOf),
            autoClose: false,
          });
        },
      },
    );
  };

  // Entitlements for modules the platform no longer offers (planned or retired) are shown, never changed here.
  const offered = new Set((catalog.data ?? []).map((m) => m.key));
  const unoffered = entitlements.filter((e) => !offered.has(e.module_key));

  return (
    <Card>
      <Title order={4} mb={4}>
        Modules
      </Title>
      <Text size="sm" c="dimmed" mb="sm">
        {canManage
          ? "What this church has. Changes apply at once and are written to the audit log."
          : "What this church has. Only a platform super admin can change it."}
      </Text>
      <Group gap="sm" mb="sm" align="center">
        <Text size="sm" fw={600}>
          Plan
        </Text>
        {canManage ? (
          <Select
            aria-label="Plan"
            size="xs"
            w={160}
            value={plan}
            allowDeselect={false}
            disabled={setPlan.isPending}
            data={PLAN_KEYS.map((key) => ({ value: key, label: PLAN_LABEL[key] }))}
            onChange={(value) => {
              if (value !== null && value !== plan && PLAN_KEYS.includes(value as PlanKey))
                setPlanTo(value as PlanKey);
            }}
          />
        ) : (
          <PlanBadge plan={plan} />
        )}
        <Text size="xs" c="dimmed">
          What the plan includes is on unless this church has a row of its own for it.
        </Text>
      </Group>
      {catalog.isPending ? (
        <LoadingState rows={4} />
      ) : catalog.isError ? (
        <ErrorState
          message="Could not load the module catalog."
          onRetry={() => {
            void catalog.refetch();
          }}
        />
      ) : (
        <Table.ScrollContainer minWidth={canManage ? 640 : 360}>
          <Table verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Module</Table.Th>
                <Table.Th>Status</Table.Th>
                {canManage ? <Table.Th>Change</Table.Th> : null}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {catalog.data.map((module) => {
                const entitlement = byKey.get(module.key);
                const busy =
                  setEntitlement.isPending && setEntitlement.variables.moduleKey === module.key;
                return (
                  <Table.Tr key={module.key}>
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {module.display_name}
                      </Text>
                      {module.dependencies.length === 0 ? null : (
                        <Text size="xs" c="dimmed">
                          Needs {module.dependencies.map(nameOf).join(", ")}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {module.is_base ? (
                        <Badge variant="light" color="gray">
                          Included
                        </Badge>
                      ) : (
                        <Stack gap={2} align="flex-start">
                          <Text size="sm" fw={500}>
                            {planStateLabel(inPlan.has(module.key), entitlement)}
                          </Text>
                          {entitlement === undefined ? null : (
                            <>
                              <StatusBadge domain="entitlement_status" value={entitlement.status} />
                              <Text size="xs" c="dimmed">
                                {stateLine(entitlement, timeZone)}
                              </Text>
                            </>
                          )}
                        </Stack>
                      )}
                    </Table.Td>
                    {canManage ? (
                      <Table.Td>
                        {module.is_base ? (
                          <Text size="xs" c="dimmed">
                            Every church has it
                          </Text>
                        ) : (
                          <Group gap="xs" wrap="nowrap">
                            {actionsFor(entitlement).map((action) => (
                              <Button
                                key={action}
                                size="xs"
                                variant={
                                  action === "canceled" || action === "suspended"
                                    ? "subtle"
                                    : "default"
                                }
                                {...(action === "canceled" ? { color: "red" } : {})}
                                style={{ flexShrink: 0 }}
                                disabled={setEntitlement.isPending}
                                loading={
                                  busy &&
                                  setEntitlement.variables.status === statusFor(action, entitlement)
                                }
                                aria-label={`${ACTION_LABEL[action](entitlement)}: ${module.display_name}`}
                                onClick={() => {
                                  if (action === "trial") {
                                    trialForm.setValues({ endsOn: addDays(today, TRIAL_DAYS) });
                                    trialForm.clearErrors();
                                    setTrialFor(module);
                                  } else if (action === "suspended" || action === "canceled") {
                                    setConfirming({ module, status: action });
                                  } else if (action === "resume" && entitlement?.ends_at != null) {
                                    apply(module, "trial", entitlement.ends_at);
                                  } else {
                                    apply(module, "active");
                                  }
                                }}
                              >
                                {ACTION_LABEL[action](entitlement)}
                              </Button>
                            ))}
                          </Group>
                        )}
                      </Table.Td>
                    ) : null}
                  </Table.Tr>
                );
              })}
              {unoffered.map((e) => (
                <Table.Tr key={e.module_key}>
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {e.module_key}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Not offered to churches
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <StatusBadge domain="entitlement_status" value={e.status} />
                  </Table.Td>
                  {canManage ? <Table.Td /> : null}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <Modal
        opened={trialFor !== null}
        onClose={() => {
          setTrialFor(null);
        }}
        title={
          trialFor === null
            ? "Trial"
            : `${byKey.get(trialFor.key)?.status === "trial" ? "Change the trial of" : "Start a trial of"} ${trialFor.display_name}`
        }
      >
        <form
          noValidate
          onSubmit={trialForm.onSubmit((values) => {
            if (trialFor !== null)
              apply(trialFor, "trial", endOfChurchDay(values.endsOn, timeZone));
          })}
        >
          <Stack gap="sm">
            <Text size="sm">
              {tenantName} gets {trialFor?.display_name} until the end of the day you choose, in the
              church&apos;s time zone ({timeZone}). After that it is hidden again, and its data is
              kept.
            </Text>
            <TextInput
              type="date"
              label="Trial ends"
              min={addDays(today, 1)}
              {...trialForm.getInputProps("endsOn")}
            />
            <Group justify="flex-end" gap="sm">
              <Button
                variant="default"
                onClick={() => {
                  setTrialFor(null);
                }}
                disabled={setEntitlement.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!trialForm.isValid()}
                loading={setEntitlement.isPending}
              >
                {byKey.get(trialFor?.key ?? "")?.status === "trial"
                  ? "Change trial"
                  : "Start trial"}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <ConfirmActionModal
        opened={confirming !== null}
        onClose={() => {
          setConfirming(null);
        }}
        title={
          confirming === null
            ? ""
            : `${confirming.status === "suspended" ? "Suspend" : "Cancel"} ${confirming.module.display_name} for ${tenantName}?`
        }
        message={
          confirming === null
            ? ""
            : `${tenantName}'s staff and members stop seeing ${confirming.module.display_name} at once. Its data is kept, and turning it on again brings it back. The change is written to the audit log.`
        }
        confirmLabel={confirming?.status === "suspended" ? "Suspend" : "Cancel module"}
        danger
        loading={setEntitlement.isPending}
        onConfirm={() => {
          if (confirming !== null) apply(confirming.module, confirming.status);
        }}
      />
      <ConfirmActionModal
        opened={planTo !== null}
        onClose={() => {
          setPlanTo(null);
        }}
        onConfirm={() => {
          if (planTo === null) return;
          setPlan.mutate(planTo, {
            onSuccess: () => {
              setPlanTo(null);
              notifications.show({
                color: "teal",
                title: `${tenantName} is on ${PLAN_LABEL[planTo]}`,
                message:
                  "What the plan includes is on now; the church's own rows still decide where they exist.",
              });
            },
            onError: (error) => {
              setPlanTo(null);
              notifications.show({
                color: "red",
                title: "The plan was not changed",
                message: describePlanError(error),
                autoClose: false,
              });
            },
          });
        }}
        title={`Move ${tenantName} to ${planTo === null ? "" : PLAN_LABEL[planTo]}?`}
        message={`${tenantName} moves from ${PLAN_LABEL[plan]} to ${planTo === null ? "" : PLAN_LABEL[planTo]}. Modules the new plan includes switch on; modules it leaves out switch off unless this church has a row of its own for them. Data is kept either way.`}
        confirmLabel="Change plan"
        loading={setPlan.isPending}
      />
    </Card>
  );
}
